import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {ClassTransformer} from 'class-transformer';
import * as gql from 'gql-query-builder';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {BaseService} from './base-service';
import {PagedModel} from '../../api/model/paged-model';
import {MetadataUnit} from '@app/graphql-typings/graphql';
import {StringUtils} from '@shared/util/general/string-utils';


class MetadataUnitImpl implements MetadataUnit {
  unit_id: number;
  unit: string;
  shared: boolean;
  customer_id: number;
  system: boolean;
}

export interface MetadataUnitSearchTerm {
  unit_id?: number;
  unit_ids?: number[];
  unit?: string;
  shared?: boolean;
  system?: boolean;
}

export const metadataUnitResolver: ResolveFn<MetadataUnit> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<MetadataUnit> => {
  return inject(MetadataUnitService).getMetadataUnit(StringUtils.stringToNumber(route.paramMap.get('id')));
};

@Injectable()
export class MetadataUnitService extends BaseService {

  public static readonly modelMapping = {MetadataUnit: MetadataUnitImpl};
  private readonly logger: AceLogger;
  private DATA_KEY = 'metadata_unit';
  private DATA_KEY_PLURAL = this.DATA_KEY + 's';
  private URL_PART = '/' + this.DATA_KEY_PLURAL;

  constructor(private http: HttpClient, private classTransformer: ClassTransformer, private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(MetadataUnitService.name);
  }

  getMetadataUnit(id: number): Observable<MetadataUnit> {
    return this.getMetadataUnits({first: 0, rows: 1}, {unit_ids: [id]}).pipe(map(result => {
      return result && result.data && result.data.length ? result.data[0] : null;
    }));
  }

  getMetadataUnits(pageInfo: {first: number, rows: number}, fields?: MetadataUnitSearchTerm): Observable<PagedModel<MetadataUnit[]>> {
    const query = gql.query({
      operation: 'metadata_units',
      fields: ['unit_id', 'unit', 'shared', 'customer_id', 'system'],
      variables: {
        unit_id: {type: GRAPHQL_TYPES.INTEGER},
        unit_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        unit: {type: GRAPHQL_TYPES.STRING},
        shared: {type: GRAPHQL_TYPES.BOOLEAN},
        system : {type: GRAPHQL_TYPES.BOOLEAN},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    });

    return this.httpUtilityService.graphQL<MetadataUnit[]>(query, this.DATA_KEY_PLURAL, Object.assign({start_index: pageInfo.first, limit: pageInfo.rows}, fields))
      .pipe(map(result => {
        const modelType = MetadataUnitService.modelMapping['MetadataUnit'];
        return {
          data: this.classTransformer.plainToInstance(modelType, result.data),
          count: result.count
        };
      }));
  }

  createMetadataUnit(metadataUnit: MetadataUnit): Observable<MetadataUnit> {
    const options = {headers: this.getHeaders()};
    return this.http.post(this.getGateway() + this.URL_PART, metadataUnit, options).pipe(map(response => {
      return this.classTransformer.plainToInstance(MetadataUnitImpl, this.httpUtilityService.extractResponseData(response, this.DATA_KEY));
    }));
  }

  updateMetadataUnit(metadataUnit: MetadataUnit): Observable<MetadataUnit> {
    const options = {headers: this.getHeaders()};
    return this.http.put(this.getGateway() + this.URL_PART + '/' + metadataUnit.unit_id, metadataUnit, options).pipe(map(response => {
      return this.classTransformer.plainToInstance(MetadataUnitImpl, this.httpUtilityService.extractResponseData(response, this.DATA_KEY));
    }));
  }

  changeSharingMetadataUnit(metadataUnit: MetadataUnit): Observable<MetadataUnit> {
    metadataUnit.shared = !metadataUnit.shared;
    return this.updateMetadataUnit(metadataUnit);
  }

  deleteMetadataUnit(id: number): Observable<number> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + this.URL_PART + '/' + id, options).pipe(map(response => {
      return this.httpUtilityService.extractResponseData(response, this.DATA_KEY)['unit_id'];
    }));
  }
}
